import { Capacitor, registerPlugin } from '@capacitor/core'
import { Filesystem } from '@capacitor/filesystem'
import type { ScreenshotPlugin } from '@capawesome/capacitor-screenshot'
import { log } from './logger'

const CLONE_TIMEOUT_MS = 3000
const RENDER_TIMEOUT_MS = 10_000
const SCROLL_ATTR = 'data-capture-scroll'
const BOX_ATTR = 'data-capture-box'
const STICKY_ATTR = 'data-capture-sticky'

function isOffscreen(r: DOMRect): boolean {
  return r.bottom < 0 || r.right < 0 || r.top > window.innerHeight || r.left > window.innerWidth
}

// How far each sticky element is currently shifted from where it would sit
// unstuck. A clone has nothing scrolled, so nothing in it sticks.
function measureStickyShifts(candidates: Element[]): [Element, string, string][] {
  const marks: [Element, string, string][] = []
  for (const el of candidates) {
    if (!(el instanceof HTMLElement) || getComputedStyle(el).position !== 'sticky') continue
    const stuck = el.getBoundingClientRect()
    const inline = el.style.position
    el.style.position = 'relative'
    const unstuck = el.getBoundingClientRect()
    el.style.position = inline
    const dx = stuck.left - unstuck.left
    const dy = stuck.top - unstuck.top
    if (dx || dy) marks.push([el, STICKY_ATTR, `${dx},${dy}`])
  }
  return marks
}

// The clone can't see live layout, so tag what it needs on the live elements:
// scroll offsets and sticky shifts (a clone loses both), and off-screen
// subtrees to prune — cloning every node's computed style is most of the
// capture time on a long schedule. A pruned element keeps its measured box so
// nothing visible moves. Rects are read before any attribute is written: a
// write between layout reads forces a relayout per element.
function markLiveElements(): { pruned: Set<Element>; unmark: () => void } {
  const marks: [Element, string, string][] = []
  const pruned = new Set<Element>()
  const visible: Element[] = []
  for (const el of document.body.querySelectorAll('*')) {
    const parent = el.parentElement
    if (parent && pruned.has(parent)) {
      pruned.add(el)
      continue
    }
    if (el.scrollTop > 0 || el.scrollLeft > 0) marks.push([el, SCROLL_ATTR, `${el.scrollLeft},${el.scrollTop}`])
    if (el.childElementCount === 0) {
      visible.push(el)
      continue
    }
    const rect = el.getBoundingClientRect()
    if (isOffscreen(rect)) {
      marks.push([el, BOX_ATTR, `${rect.width},${rect.height}`])
      pruned.add(el)
    } else {
      visible.push(el)
    }
  }
  marks.push(...measureStickyShifts(visible))
  marks.forEach(([el, name, value]) => el.setAttribute(name, value))
  return { pruned, unmark: () => marks.forEach(([el, name]) => el.removeAttribute(name)) }
}

function prepareClone(cloned: Node) {
  if (!(cloned instanceof HTMLElement)) return
  // Rendered inside a foreignObject, a backdrop blur smears a box over its
  // corner of the shot instead of blurring what's behind it.
  if (cloned.style.backdropFilter) cloned.style.backdropFilter = 'none'
  const box = cloned.getAttribute(BOX_ATTR)
  if (box) {
    const [w, h] = box.split(',')
    Object.assign(cloned.style, { width: `${w}px`, height: `${h}px`, boxSizing: 'border-box', overflow: 'hidden' })
    return
  }
  const sticky = cloned.getAttribute(STICKY_ATTR)
  if (sticky) {
    // Relative offsets, not translate: they compose with the translate a
    // scrolled ancestor applies to its children.
    const [dx, dy] = sticky.split(',')
    Object.assign(cloned.style, { position: 'relative', left: `${dx}px`, top: `${dy}px`, right: 'auto', bottom: 'auto' })
  }
  if (cloned.tagName === 'BODY') {
    // A margin, not a transform: a transform would become the containing block
    // for the fixed nav bar and drag it off-screen with the content.
    // The foreignObject applies the UA's default 8px body margin; reset it first.
    cloned.style.margin = '0'
    cloned.style.marginTop = `${-window.scrollY}px`
    return
  }
  const offset = cloned.getAttribute(SCROLL_ATTR)
  if (!offset) return
  cloned.removeAttribute(SCROLL_ATTR)
  const [x, y] = offset.split(',')
  for (const child of cloned.children) {
    if (child instanceof HTMLElement || child instanceof SVGElement) child.style.translate = `${-x}px ${-y}px`
  }
}

// Registered here rather than imported from the package: its web fallback
// bundles html2canvas, and the web already has the DOM renderer below.
const NativeScreenshot = registerPlugin<ScreenshotPlugin>('Screenshot')
const hasNativeScreenshot = () => Capacitor.isPluginAvailable('Screenshot')

/** Warms the DOM renderer chunk so the first shake doesn't wait on loading it. */
export function preloadViewportCapture() {
  if (hasNativeScreenshot()) return
  void import('modern-screenshot').catch(() => {})
}

/**
 * Starts a best-effort JPEG capture of the visible viewport. Resolves once the
 * page has been captured (natively) or cloned (DOM renderer) — from then on the
 * page can change (a dialog can open) without showing up in the shot — with
 * `screenshot`, which settles once the image is ready: null on failure or
 * timeout, so a report never waits on it. Shells with the Screenshot plugin
 * snapshot the web view natively; web and older shells lazy-load the DOM
 * renderer, which is only needed after a shake.
 */
export async function startViewportCapture(): Promise<{ screenshot: Promise<Blob | null> }> {
  if (hasNativeScreenshot()) {
    try {
      const { uri } = await NativeScreenshot.take()
      return { screenshot: readNativeScreenshot(uri) }
    } catch (err) {
      log('rage-shake', 'native screenshot failed', { message: err instanceof Error ? err.message : String(err) })
    }
  }
  return startDomCapture()
}

async function readNativeScreenshot(uri: string): Promise<Blob | null> {
  try {
    const res = await fetch(Capacitor.convertFileSrc(uri))
    return res.ok ? await res.blob() : null
  } catch (err) {
    log('rage-shake', 'native screenshot read failed', { message: err instanceof Error ? err.message : String(err) })
    return null
  } finally {
    // The plugin writes each shot to the cache directory and never removes it.
    void Filesystem.deleteFile({ path: uri }).catch(() => {})
  }
}

async function startDomCapture(): Promise<{ screenshot: Promise<Blob | null> }> {
  const { pruned, unmark } = markLiveElements()
  let abandoned = false
  let markCloned!: () => void
  const cloned = new Promise<void>(resolve => (markCloned = resolve))

  const render = import('modern-screenshot')
    .then(({ domToBlob }) =>
      domToBlob(document.body, {
        width: window.innerWidth,
        height: window.innerHeight,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        type: 'image/jpeg',
        quality: 0.85,
        // Per asset: one slow image renders blank rather than sinking the capture.
        timeout: 2500,
        backgroundColor: getComputedStyle(document.body).backgroundColor,
        filter: node => !(node.parentElement && pruned.has(node.parentElement)),
        onCloneEachNode: prepareClone,
        onCloneNode: () => {
          unmark()
          markCloned()
        },
      }),
    )
    .catch((err: unknown) => {
      log('rage-shake', 'screenshot failed', { message: err instanceof Error ? err.message : String(err) })
      return null
    })
    .finally(() => {
      unmark()
      markCloned()
    })

  // A clone still running when the caller moves on could pick up whatever
  // opens next, so a slow clone is dropped rather than waited for.
  const cloneTimedOut = await Promise.race([
    cloned.then(() => false),
    sleep(CLONE_TIMEOUT_MS).then(() => true),
  ])
  if (cloneTimedOut) {
    abandoned = true
    log('rage-shake', 'screenshot clone timed out')
  }

  const screenshot = Promise.race([render, sleep(RENDER_TIMEOUT_MS).then(() => null)]).then(blob => {
    if (!blob && !abandoned) log('rage-shake', 'screenshot render timed out')
    return abandoned ? null : blob
  })
  return { screenshot }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}
